/**
 * Route Engine — Deterministic route generation.
 * Platform-agnostic: uses WaypointDataProvider instead of DatabaseAdapter.
 */
import type { ResolvedWaypoint, RouteWaypoint, RouteLeg, Route, RouteParams, LatLng, LegMetrics, WindData } from './types';
import { FlightPlanError } from './errors';

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const EARTH_RADIUS_NM = 3440.065;

export interface RouteEngineConfig {
  defaultMagVar?: number;
}

export class RouteEngine {
  private config: RouteEngineConfig;

  constructor(config?: RouteEngineConfig) {
    this.config = config ?? {};
  }

  async generateRoute(params: RouteParams): Promise<Route> {
    const { departure, destination, intermediates, cruiseAltitude, trueAirspeed } = params;
    const all: ResolvedWaypoint[] = [departure, ...intermediates, destination];
    return this.buildRoute(
      all.map((wp, i) => ({ ...wp, sequenceIndex: i, plannedAltitude: cruiseAltitude })),
      trueAirspeed,
    );
  }

  computeLegMetrics(from: LatLng, to: LatLng, tas: number, wind?: WindData): LegMetrics {
    const distanceNm = this.haversineDistanceNm(from, to);
    const trueCourse = this.trueBearing(from, to);
    const magVar = this.config.defaultMagVar ?? 0;
    const magneticCourse = this.norm(trueCourse - magVar);
    let groundSpeed = tas;
    if (wind && wind.speed > 0) {
      const wca = Math.asin((wind.speed * Math.sin(DEG_TO_RAD * (wind.direction - trueCourse))) / tas) * RAD_TO_DEG;
      const heading = trueCourse + wca;
      groundSpeed = tas * Math.cos(wca * DEG_TO_RAD) + wind.speed * Math.cos(DEG_TO_RAD * (wind.direction - heading));
      groundSpeed = Math.max(groundSpeed, 1);
    }
    return { distanceNm, trueCourse, magneticCourse, estimatedTimeMinutes: (distanceNm / groundSpeed) * 60, groundSpeed };
  }

  async recomputeSegments(route: Route, segmentIndex: number, newPoint: LatLng): Promise<Route> {
    if (segmentIndex < 0 || segmentIndex >= route.legs.length) {
      throw new FlightPlanError('Invalid segment index', 'ROUTE_NOT_FOUND', { segmentIndex });
    }
    const leg = route.legs[segmentIndex];
    const newWp: RouteWaypoint = {
      identifier: `USR${Date.now()}`, name: 'Drag Waypoint', type: 'user_defined',
      latitude: newPoint.latitude, longitude: newPoint.longitude, source: 'user',
      sequenceIndex: segmentIndex + 1, plannedAltitude: leg.plannedAltitude,
    };
    const wps = [...route.waypoints];
    wps.splice(segmentIndex + 1, 0, newWp);
    const reindexed = wps.map((wp, i) => ({ ...wp, sequenceIndex: i }));
    const tas = route.totalDistanceNm > 0 && route.totalTimeMinutes > 0
      ? (route.totalDistanceNm / route.totalTimeMinutes) * 60 : 120;
    return this.buildRoute(reindexed, tas);
  }

  haversineDistanceNm(from: LatLng, to: LatLng): number {
    const dLat = DEG_TO_RAD * (to.latitude - from.latitude);
    const dLon = DEG_TO_RAD * (to.longitude - from.longitude);
    const lat1 = DEG_TO_RAD * from.latitude;
    const lat2 = DEG_TO_RAD * to.latitude;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return EARTH_RADIUS_NM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  trueBearing(from: LatLng, to: LatLng): number {
    const lat1 = DEG_TO_RAD * from.latitude;
    const lat2 = DEG_TO_RAD * to.latitude;
    const dLon = DEG_TO_RAD * (to.longitude - from.longitude);
    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    return this.norm(Math.atan2(y, x) * RAD_TO_DEG);
  }

  buildRoute(waypoints: RouteWaypoint[], tas: number): Route {
    const legs: RouteLeg[] = [];
    for (let i = 0; i < waypoints.length - 1; i++) {
      const from = waypoints[i]; const to = waypoints[i + 1];
      const m = this.computeLegMetrics(from, to, tas);
      legs.push({
        from, to, distanceNm: m.distanceNm, trueCourse: m.trueCourse,
        magneticCourse: m.magneticCourse, estimatedTimeMinutes: m.estimatedTimeMinutes,
        plannedAltitude: to.plannedAltitude,
      });
    }
    return {
      waypoints, legs,
      totalDistanceNm: legs.reduce((s, l) => s + l.distanceNm, 0),
      totalTimeMinutes: legs.reduce((s, l) => s + l.estimatedTimeMinutes, 0),
    };
  }

  private norm(deg: number): number { return ((deg % 360) + 360) % 360; }
}
